import type { SharedAttributeJSON } from "@/features/shared_attributes/types";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { StatefulTrackerViewT } from "@patient-watch/types";

import {
    isDisplayableHospitalNumber,
    isHospitalNumberMissing,
    hasAnyMissingReferenceDate,
    patientNeedsHospitalNumberSetup,
    patientNeedsSideSetupRoute,
    type PatientSetupStepPath,
} from "./missingDetailsBuilders";

function sharedAttributesRows(diary: StatefulDiaryT): SharedAttributeJSON[] {
    const rows: SharedAttributeJSON[] = [];
    const add = (sa: SharedAttributeJSON | null | undefined) => {
        if (sa) rows.push(sa);
    };
    add(diary.shared_attributes as SharedAttributeJSON | undefined);
    for (const tracker of diary.trackers_view ?? []) {
        add((tracker as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }
    for (const event of diary.events_view ?? []) {
        add((event as { shared_attributes?: SharedAttributeJSON | null })?.shared_attributes);
    }
    return rows;
}

/** Hospital step was part of this diary's setup flow (pending, entered, or skipped). */
export function diaryIncludesHospitalSetup(diary: StatefulDiaryT): boolean {
    if (diary.require_hospital_number === false) {
        return sharedAttributesRows(diary).some((row) => isDisplayableHospitalNumber(row.hospital_number));
    }
    if (patientNeedsHospitalNumberSetup(diary)) return true;
    return sharedAttributesRows(diary).some((row) => !isHospitalNumberMissing(row.hospital_number));
}

/** The template filled the procedure date with the diary start date, so the patient was never asked for it. */
function isAutoSetReferenceDate(row: SharedAttributeJSON): boolean {
    if (row.reference_date == null || row.start_date == null) return false;
    const reference = new Date(row.reference_date).getTime();
    return !Number.isNaN(reference) && reference === new Date(row.start_date).getTime();
}

/**
 * Reference-date step is part of this diary's setup flow when the patient still
 * has to supply it, or when a saved date sits alongside hospital number or side.
 * A date already on the diary, with hospital number turned off and no side, is
 * not a patient setup step.
 */
export function diaryIncludesReferenceDateSetup(diary: StatefulDiaryT): boolean {
    if (hasAnyMissingReferenceDate(diary)) return true;
    if (diary.require_hospital_number === false && !diaryIncludesSideSetup(diary)) return false;
    return sharedAttributesRows(diary).some((row) => row.reference_date != null && !isAutoSetReferenceDate(row));
}

/** Side step was part of this diary's setup flow (pending or already saved). */
export function diaryIncludesSideSetup(diary: StatefulDiaryT): boolean {
    if (patientNeedsSideSetupRoute(diary)) return true;

    const hasSideSpecificTracker = (diary.trackers_view ?? []).some((tracker) =>
        Boolean((tracker as StatefulTrackerViewT).form?.is_side_specific)
    );
    if (hasSideSpecificTracker) return true;

    return (diary.events_view ?? []).some(
        (event) =>
            (event?.surgeries_view ?? []).length > 0 ||
            (event?.injections_view ?? []).length > 0 ||
            (event?.shockwaves_view ?? []).length > 0
    );
}

/** Setup sub-steps that apply to this diary, in visit order (demo-style wizard progress). */
export function getPatientSetupStepSequence(diary: StatefulDiaryT): PatientSetupStepPath[] {
    const steps: PatientSetupStepPath[] = [];
    if (diaryIncludesHospitalSetup(diary)) steps.push("setup/hospital-number");
    if (diaryIncludesReferenceDateSetup(diary)) steps.push("setup/reference-date");
    if (diaryIncludesSideSetup(diary)) steps.push("setup/side");
    return steps;
}
